/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    CashBoxesAction,
    CashBoxesActionType,
    fetchCashBoxesListPageErrorAction,
    fetchCashBoxesListPageSuccessAction,
    downloadCashBoxesListAsExcelSuccessAction,
    downloadCashBoxesListAsExcelErrorAction,
    fetchCashRegisterVersionsSuccessAction,
} from '../../actions/CashBoxRegistry/cashBoxesActions';
import { CashBoxesState } from '../../reducers/CashBoxRegistry/cashBoxesReducer';
import { CashBoxesListRequestParams } from '../../api/cashBoxes/cashBoxesClient.typings';
import { switchMap, catchError, map, takeUntil } from 'rxjs/operators';
import { cashboxesClient } from '../../api/cashBoxes/cashboxesClient';
import { of, from, NEVER } from 'rxjs';
import { AxiosError } from 'axios';
import { parseError } from '../../../utils/utils';
import { i18n } from '../../translation/i18n';
import { cashRegisterModelsClient } from '../../api/cashRegisterModels/cashRegisterModelsClient';

export const fetchCashBoxesEpic: Epic<CashBoxesAction> = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.FETCH_NEXT_PAGE_START, CashBoxesActionType.APPLY_FILTERS),
        switchMap(() => {
            const queryParams = makeCashBoxesListRequestParamsFromState(state.value.cashBoxesReducer);

            return from(cashboxesClient.listCashBoxes(queryParams)).pipe(
                map((list) => fetchCashBoxesListPageSuccessAction(list)),

                catchError((err: AxiosError) =>
                    of(
                        fetchCashBoxesListPageErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashboxesListNotFound')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE))),
            );
        }),
    );
};

function makeCashBoxesListRequestParamsFromState(state: CashBoxesState): CashBoxesListRequestParams {
    return {
        ...state.filterParams,
        limit: state.limit,
        afterKeyTaxNumber: state.afterKeyTaxNumber,
    };
}

export const cashBoxesListDownloadEpic: Epic<CashBoxesAction> = (action$, state) => {
    return action$.pipe(
        ofType(CashBoxesActionType.DOWNLOAD_EXCEL),
        switchMap(() => {
            return from(
                cashboxesClient.downloadCashBoxesAsExcel({ ...state.value.cashBoxesReducer.filterParams }),
            ).pipe(
                map(() => downloadCashBoxesListAsExcelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadCashBoxesListAsExcelErrorAction(
                            parseError(err, i18n.t('downloadings.errors.requestToCreateFileFailed')),
                        ),
                    ),
                ),
                takeUntil(action$.pipe(ofType(CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE))),
            );
        }),
    );
};

export const fetchCashRegisterVersionsListEpic: Epic<CashBoxesAction> = (action$) => {
    return action$.pipe(
        ofType(CashBoxesActionType.FETCH_CASH_REGISTERS_VERSIONS_LIST_START),
        switchMap((action) => {
            return from(cashRegisterModelsClient.getModelSoftwareVersionNames(action.payload)).pipe(
                map((list) => fetchCashRegisterVersionsSuccessAction(list)),

                catchError(() => NEVER),

                takeUntil(action$.pipe(ofType(CashBoxesActionType.EXIT_REGISTRY_CASHBOXES_LIST_PAGE))),
            );
        }),
    );
};
